<template>
  <div class="reservation-column-edit">
    <validation-observer
      class="validation-observer"
      v-slot="{ invalid }"
      ref="form"
    >
      <div class="title">予約列編集</div>
      <div class="contents">
        <div class="input-wrap">
          <div class="heading">基本項目</div>
          <div class="content-area">
            <reservation-column-input-form
              class="input-form"
              :reservationColumn="editReservationColumn"
              :reservationColumnGroups="reservationColumnGroups"
              :staffs="staffs"
              @input-staff="inputStaff"
            />
          </div>
        </div>
        <div class="button-wrap">
          <base-button-medium-white class="button" @click="openShowPage"
            >戻る</base-button-medium-white
          >
          <base-button-medium-orange
            class="button"
            v-if="lookOnlyFlg === 0"
            :disabled="invalid || !allValidFlg || waitFlg"
            @click="updateData"
            >登録</base-button-medium-orange
          >
        </div>
      </div>
      <announce-popup
        v-if="alertFlg"
        :title="title"
        :buttons="buttons"
        :type="type"
        @close="closeAlert"
        >{{ popupMessage }}</announce-popup
      >
      <unsaved-leave-popup />
    </validation-observer>
  </div>
</template>

<script>
import ReservationColumnInputForm from '@/components/parts/organisms/ReservationColumnInputForm'
import BaseButtonMediumWhite from '@/components/parts/atoms/BaseButtonMediumWhite'
import BaseButtonMediumOrange from '@/components/parts/atoms/BaseButtonMediumOrange'
import AnnouncePopup from '@/components/popups/AnnouncePopup'
import UnsavedLeavePopup from '@/components/popups/UnsavedLeavePopup'
import CheckInputDifference from '@/components/mixins/CheckInputDifference'
import { ValidationObserver } from 'vee-validate'
import { VALID_COLOR_CODE_REGEX } from '@/utils/define'
import { mapGetters } from 'vuex'

export default {
  name: 'ReservationColumnEdit',

  components: {
    ReservationColumnInputForm,
    BaseButtonMediumWhite,
    BaseButtonMediumOrange,
    AnnouncePopup,
    UnsavedLeavePopup,
    ValidationObserver
  },

  mixins: [CheckInputDifference],

  props: {
    id: { type: Number }
  },

  data() {
    return {
      editReservationColumn: {},
      reservationColumnGroups: this.$store.getters[
        'reservationColumnGroups/selectData'
      ],
      staffs: this.$store.getters['staffs/selectDataZero'],
      noDataFlg: false,
      alertFlg: false,
      waitFlg: false,
      type: '',
      title: '',
      popupMessage: '',
      buttons: ['閉じる'],
      initialStaffId: 0
    }
  },

  computed: {
    ...mapGetters({
      lookOnlyFlg: 'auth/lookOnlyFlg',
      reservationPurposes: 'reservationPurposes/getData'
    }),
    groupIdValidFlg() {
      return this.reservationColumnGroups.some(
        o => o.id === this.editReservationColumn.reservationColumnGroupId
      )
    },
    colorValidFlg() {
      return VALID_COLOR_CODE_REGEX.test(this.editReservationColumn.color)
    },
    allValidFlg() {
      return this.groupIdValidFlg && this.colorValidFlg
    }
  },

  created() {
    this.editReservationColumn = {
      ...this.$store.getters['reservationColumns/getDataById'](this.id)
    }
    this.initialStaffId = this.editReservationColumn.staffId
    this.mixinInputData = this.editReservationColumn
    this.mixinSetInitialData()
  },

  methods: {
    openShowPage() {
      this.$router.push({
        name: 'reservation-column-show',
        params: this.id
      })
    },
    async resetReservationColumnGroups() {
      this.editReservationColumn.reservationColumnGroupId = ''
      this.reservationColumnGroups = this.$store.getters[
        'reservationColumnGroups/selectData'
      ]
    },
    inputStaff() {
      if (
        this.editReservationColumn.staffId !== 0 &&
        this.initialStaffId !== this.editReservationColumn.staffId
      ) {
        const purposes = this.reservationPurposes.filter(p =>
          p.reservationColumnsIds.includes(this.editReservationColumn.id)
        )
        const purposeNames = purposes
          .filter(
            p => !p.staffsIds.includes(this.editReservationColumn.staffId)
          )
          .map(p => p.name)
        if (purposeNames.length > 0) {
          this.alertFlg = true
          this.title = '来院目的の再設定'
          this.type = 'alert'
          this.popupMessage =
            'スタッフの変更により、来院目的\n' +
            purposeNames.join(',') +
            '\nが無効になる可能性があります。\n登録後、確認をお願いします。'
        }
      }
    },
    async updateData() {
      this.waitFlg = true
      const result = await this.$store.dispatch(
        'reservationColumns/update',
        this.editReservationColumn
      )
      if (result === true) {
        this.type = 'success'
        this.title = '完了'
        this.popupMessage = '編集しました'
        this.mixinSetInitialData()
      } else {
        this.type = 'failure'
        this.title = '失敗'
        if (
          result === 'no reservationColumn' ||
          result === 'no data in clinic'
        ) {
          this.popupMessage =
            '編集に失敗しました。\n編集中の予約列は既に削除されています。'
          this.noDataFlg = true
        } else if (result === 'no reservationColumnGroup') {
          this.popupMessage =
            '編集に失敗しました。\n選択した予約列グループは既に削除されています。\n他の予約列グループを選択して下さい。'
          await this.resetReservationColumnGroups()
        } else {
          this.popupMessage = '編集に失敗しました'
        }
      }
      this.waitFlg = false
      this.alertFlg = true
    },
    closeAlert() {
      this.alertFlg = false
      if (this.type === 'success') {
        this.$store.dispatch('petorelu/okLeave')
        this.openShowPage()
      }
      if (this.noDataFlg) {
        this.$store.dispatch('petorelu/okLeave')
        this.$router.push({ name: `reservation-columns-tab` })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.reservation-column-edit {
  > .validation-observer {
    > .title {
      font-size: 20px;
      font-weight: bold;
      border-bottom: 1px solid #{$light-grey};
      padding-bottom: 20px;
    }
    > .contents {
      margin-top: 20px;
      > .input-wrap {
        > .heading {
          font-size: 15px;
          font-weight: bold;
          border-bottom: 1px solid #{$light-grey};
          padding-bottom: 20px;
        }
        > .content-area {
          margin-top: 25px;
          > .input-form {
            width: 600px;
          }
        }
      }
      > .button-wrap {
        display: flex;
        justify-content: center;
        margin: 60px 0;
        > .button {
          margin: 0 16px;
        }
      }
    }
  }
}
</style>
